import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Header from "components/common/Header/Header";
import SideBar from "components/common/SideBar/SideBarNew";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import CustomButton from "components/common/Button/CustomButton";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import { LoginContext } from "helpers/LoginContext";
import employeeApi from "store/services/employee.services";
import {
  canDownloadWyzedCertificate,
  canStartWorkerWyzedTraining,
  canViewWorkerWyzedTraining,
} from "helpers/RolesPermission";
import { formatDateToAustralian } from "Utils/utils";
import {
  HiOutlineAcademicCap,
  HiOutlineDocumentDownload,
  HiOutlineExternalLink,
  HiChevronUp,
  HiChevronDown,
} from "react-icons/hi";
import { FiSearch, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import FileSaver from "file-saver";
import WyzedBrandIcon from "components/common/WyzedBrandIcon/WyzedBrandIcon";

const STATUS_LABELS = {
  assigned: "Assigned",
  in_progress: "In Progress",
  completed: "Completed",
  expired: "Expired",
};

const STATUS_COLORS = {
  assigned: "bg-slate-100 text-slate-700 ring-slate-200",
  in_progress: "bg-sky-50 text-sky-700 ring-sky-200",
  completed: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  expired: "bg-rose-50 text-rose-700 ring-rose-200",
};

const STATUS_SORT_ORDER = {
  assigned: 0,
  in_progress: 1,
  completed: 2,
  expired: 3,
};

const STATUS_FILTER_OPTIONS = [
  { id: "all", label: "All" },
  { id: "assigned", label: "Assigned" },
  { id: "in_progress", label: "In Progress" },
  { id: "completed", label: "Completed" },
  { id: "expired", label: "Expired" },
];

const SortableHeader = ({ label, sortKey, activeSort, onSort }) => {
  const isActive = activeSort.key === sortKey;

  return (
    <th className="px-5 py-3">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded"
      >
        <span>{label}</span>
        <span className="inline-flex flex-col -space-y-1.5">
          <HiChevronUp
            className={`h-3 w-3 ${
              isActive && activeSort.direction === "asc"
                ? "text-indigo-600"
                : "text-slate-300"
            }`}
          />
          <HiChevronDown
            className={`h-3 w-3 ${
              isActive && activeSort.direction === "desc"
                ? "text-indigo-600"
                : "text-slate-300"
            }`}
          />
        </span>
      </button>
    </th>
  );
};

const CourseProgressBar = ({ completed, total }) => {
  if (!total || total <= 0) {
    return <span className="text-slate-400 text-xs">—</span>;
  }

  const percent = Math.min(100, Math.round((completed / total) * 100));

  return (
    <div className="flex items-center gap-3 min-w-[160px]">
      <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-indigo-500 transition-all duration-300"
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="text-xs font-medium text-slate-600 tabular-nums whitespace-nowrap">
        {completed}/{total}
      </span>
    </div>
  );
};

const WorkerWyzedTraining = () => {
  const { sideBarPadding, isUserGracePeriodOver } = useContext(LoginContext);
  const canView = canViewWorkerWyzedTraining();
  const canDownload = canDownloadWyzedCertificate();
  const canStart = canStartWorkerWyzedTraining();

  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [isLinked, setIsLinked] = useState(false);
  const [wyzedEmail, setWyzedEmail] = useState(null);
  const [training, setTraining] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  const [startingId, setStartingId] = useState(null);
  const [startConfirmRow, setStartConfirmRow] = useState(null);
  const [trainingSearch, setTrainingSearch] = useState("");
  const [trainingStatusFilter, setTrainingStatusFilter] = useState("all");
  const [trainingSort, setTrainingSort] = useState({ key: null, direction: "asc" });

  const loadTraining = useCallback(async () => {
    if (!canView) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await employeeApi.getWorkerWyzedTraining();
      const data = res?.data ?? {};
      setConnected(Boolean(data.connected));
      setIsLinked(Boolean(data.is_linked));
      setWyzedEmail(data.wyzed_email ?? null);
      setTraining(Array.isArray(data.training) ? data.training : []);
    } catch (err) {
      toast.error(err?.message ?? "Failed to load Wyzed training.");
      setTraining([]);
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    loadTraining();
  }, [loadTraining]);

  const trainingSummary = useMemo(() => {
    const completed = training.filter((r) => r.status === "completed").length;
    const inProgress = training.filter((r) => r.status === "in_progress").length;
    return { total: training.length, completed, inProgress };
  }, [training]);

  const statusCounts = useMemo(() => {
    const counts = { all: training.length };
    Object.keys(STATUS_LABELS).forEach((status) => {
      counts[status] = training.filter((row) => row.status === status).length;
    });
    return counts;
  }, [training]);

  const filteredTraining = useMemo(() => {
    let rows = training;

    if (trainingStatusFilter !== "all") {
      rows = rows.filter((row) => row.status === trainingStatusFilter);
    }

    const query = trainingSearch.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((row) => {
      const statusLabel = (STATUS_LABELS[row.status] ?? row.status ?? "").toLowerCase();
      const completedDate = row.completed_at
        ? formatDateToAustralian(row.completed_at).toLowerCase()
        : "";
      const progress = `${row.progress_completed}/${row.progress_total}`.toLowerCase();

      return (
        (row.course_name ?? "").toLowerCase().includes(query) ||
        statusLabel.includes(query) ||
        completedDate.includes(query) ||
        progress.includes(query)
      );
    });
  }, [training, trainingSearch, trainingStatusFilter]);

  const hasActiveTrainingFilters =
    Boolean(trainingSearch.trim()) || trainingStatusFilter !== "all";

  const clearTrainingFilters = () => {
    setTrainingSearch("");
    setTrainingStatusFilter("all");
  };

  const displayedTraining = useMemo(() => {
    const rows = [...filteredTraining];
    if (!trainingSort.key) {
      // Default: incomplete first, then most recently completed.
      return rows.sort((a, b) => {
        const aDone = a.status === "completed" ? 1 : 0;
        const bDone = b.status === "completed" ? 1 : 0;
        if (aDone !== bDone) return aDone - bDone;
        const aDate = a.completed_at ? Date.parse(a.completed_at) : 0;
        const bDate = b.completed_at ? Date.parse(b.completed_at) : 0;
        return bDate - aDate;
      });
    }

    const dir = trainingSort.direction === "asc" ? 1 : -1;

    return rows.sort((a, b) => {
      switch (trainingSort.key) {
        case "course":
          return (
            dir *
            (a.course_name ?? "").localeCompare(b.course_name ?? "", undefined, {
              sensitivity: "base",
            })
          );
        case "status": {
          const orderA = STATUS_SORT_ORDER[a.status] ?? 99;
          const orderB = STATUS_SORT_ORDER[b.status] ?? 99;
          if (orderA !== orderB) {
            return dir * (orderA - orderB);
          }
          return (
            dir *
            (STATUS_LABELS[a.status] ?? a.status ?? "").localeCompare(
              STATUS_LABELS[b.status] ?? b.status ?? "",
              undefined,
              { sensitivity: "base" }
            )
          );
        }
        case "progress": {
          const percentA =
            a.progress_total > 0
              ? a.progress_completed / a.progress_total
              : a.status === "completed"
                ? 1
                : 0;
          const percentB =
            b.progress_total > 0
              ? b.progress_completed / b.progress_total
              : b.status === "completed"
                ? 1
                : 0;
          if (percentA !== percentB) {
            return dir * (percentA - percentB);
          }
          return dir * ((a.progress_completed ?? 0) - (b.progress_completed ?? 0));
        }
        case "completed": {
          const dateA = a.completed_at ? new Date(a.completed_at).getTime() : null;
          const dateB = b.completed_at ? new Date(b.completed_at).getTime() : null;
          if (dateA === null && dateB === null) return 0;
          if (dateA === null) return dir;
          if (dateB === null) return -dir;
          return dir * (dateA - dateB);
        }
        default:
          return 0;
      }
    });
  }, [filteredTraining, trainingSort]);

  const handleTrainingSort = (key) => {
    setTrainingSort((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const openWyzedPortal = async (row) => {
    if (!canStart || isUserGracePeriodOver) return;

    try {
      setStartingId(row.id);
      let url = row.training_url;
      if (!url) {
        const res = await employeeApi.startWorkerWyzedTraining(row.id);
        url = res?.data?.training_url;
        if (res?.data?.wyzed_email) {
          setWyzedEmail(res.data.wyzed_email);
        }
      }
      if (!url) {
        throw new Error("Training link is not available.");
      }
      window.open(url, "_blank", "noopener,noreferrer");
      setStartConfirmRow(null);
    } catch (err) {
      toast.error(err?.message ?? "Failed to open Wyzed training.");
    } finally {
      setStartingId(null);
    }
  };

  const handleStartTraining = (row) => {
    if (!canStart || isUserGracePeriodOver) return;
    setStartConfirmRow(row);
  };

  const handleDownloadCertificate = async (row) => {
    if (!canDownload || !row?.can_download_certificate || isUserGracePeriodOver) {
      return;
    }

    try {
      setDownloadingId(row.id);
      const response = await employeeApi.downloadWorkerWyzedCertificate(row.id);
      const contentType = response.headers["content-type"] || "application/pdf";

      if (contentType.includes("application/json")) {
        const text = await response.data.text();
        const payload = JSON.parse(text);
        throw new Error(payload?.message ?? "Failed to download certificate.");
      }

      const blob = new Blob([response.data], { type: contentType });
      const safeName =
        String(row.course_name)
          .replace(/[^\w\s-]/g, "")
          .trim()
          .replace(/\s+/g, "_") || "certificate";
      FileSaver.saveAs(blob, `${safeName}.pdf`);
      toast.success("Certificate downloaded.");
    } catch (err) {
      let message = err?.message ?? "Failed to download certificate.";
      const data = err?.response?.data;
      if (data instanceof Blob && data.type?.includes("json")) {
        try {
          const payload = JSON.parse(await data.text());
          message = payload?.message ?? message;
        } catch (_) {}
      }
      toast.error(message);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="min-full">
      <SideBar />
      <div
        className={`${
          sideBarPadding ? "lg:pl-[4rem] duration-300" : "lg:pl-64 duration-300"
        } flex flex-col`}
      >
        <main className="flex-1 bg-gray-50">
          <Header title="Wyzed Training" />
          <div className="px-6 py-6">
            {!canView ? (
              <div className="mx-auto max-w-lg rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900">Access restricted</h3>
                <p className="mt-2 text-sm text-slate-600">
                  You do not have permission to view Wyzed training.
                </p>
              </div>
            ) : loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <CircularLoader
                  classes="flex justify-center items-center"
                  classloader="loader-l"
                />
                <p className="mt-4 text-sm text-slate-500">Loading Wyzed training…</p>
              </div>
            ) : !connected ? (
              <div className="mx-auto max-w-lg rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
                  <HiOutlineAcademicCap className="h-7 w-7 text-slate-500" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Wyzed not connected
                </h3>
                <p className="mt-2 text-sm text-slate-600">
                  Ask your administrator to connect Wyzed in Account Settings →
                  Integrations.
                </p>
              </div>
            ) : !isLinked ? (
              <div className="mx-auto max-w-lg rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
                <WyzedBrandIcon className="mx-auto h-10 w-10" />
                <h3 className="mt-3 text-lg font-semibold text-slate-900">
                  Profile not linked
                </h3>
                <p className="mt-2 text-sm text-slate-600">
                  Ask your manager to map your Vertex360 worker profile to Wyzed.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="flex flex-wrap items-center gap-4 px-5 py-5">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-indigo-50 ring-1 ring-indigo-100">
                      <WyzedBrandIcon className="h-7 w-7" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Wyzed training
                      </p>
                      <p className="mt-1 text-lg font-semibold text-slate-900">
                        My courses
                      </p>
                      <p className="mt-1 text-sm text-slate-500">
                        View progress, start courses in Wyzed, and download certificates
                        when available.
                      </p>
                    </div>
                    {training.length > 0 && (
                      <div className="flex gap-6 border-t border-slate-100 pt-4 sm:border-t-0 sm:pt-0 sm:border-l sm:pl-6">
                        <div className="text-center">
                          <p className="text-2xl font-semibold text-slate-900">
                            {trainingSummary.total}
                          </p>
                          <p className="text-xs text-slate-500">Courses</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-semibold text-emerald-600">
                            {trainingSummary.completed}
                          </p>
                          <p className="text-xs text-slate-500">Completed</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-semibold text-sky-600">
                            {trainingSummary.inProgress}
                          </p>
                          <p className="text-xs text-slate-500">In progress</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="border-b border-slate-100 px-5 py-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-slate-800">
                          Training courses
                        </h3>
                        <p className="mt-0.5 text-sm text-slate-500">
                          Progress from your Wyzed learner account.
                        </p>
                      </div>
                      {training.length > 0 && (
                        <div className="relative w-full sm:w-72 shrink-0">
                          <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                          <input
                            type="text"
                            value={trainingSearch}
                            onChange={(e) => setTrainingSearch(e.target.value)}
                            placeholder="Search courses…"
                            className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-8 text-sm text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                          {trainingSearch && (
                            <button
                              type="button"
                              onClick={() => setTrainingSearch("")}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                              aria-label="Clear search"
                            >
                              <FiX className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {training.length > 0 && (
                      <div className="mt-3 flex flex-col gap-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-medium text-slate-500 mr-1">
                            Status:
                          </span>
                          {STATUS_FILTER_OPTIONS.map((option) => {
                            const count = statusCounts[option.id] ?? 0;
                            const isActive = trainingStatusFilter === option.id;
                            return (
                              <button
                                key={option.id}
                                type="button"
                                onClick={() => setTrainingStatusFilter(option.id)}
                                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                                  isActive
                                    ? "bg-indigo-600 text-white"
                                    : "bg-white text-slate-600 ring-1 ring-slate-300 hover:bg-slate-50"
                                }`}
                              >
                                {option.label}
                                <span
                                  className={
                                    isActive ? "text-indigo-200" : "text-slate-400"
                                  }
                                >
                                  {" "}
                                  ({count})
                                </span>
                              </button>
                            );
                          })}
                          {hasActiveTrainingFilters && (
                            <button
                              type="button"
                              onClick={clearTrainingFilters}
                              className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                            >
                              Clear filters
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">
                          {hasActiveTrainingFilters
                            ? `Showing ${displayedTraining.length} of ${training.length} courses`
                            : `${training.length} course${
                                training.length === 1 ? "" : "s"
                              }`}
                        </p>
                      </div>
                    )}
                  </div>

                  {training.length === 0 ? (
                    <div className="px-5 py-12 text-center">
                      <p className="text-sm text-slate-600">No training records yet.</p>
                      <p className="mt-1 text-sm text-slate-500">
                        Once courses are assigned in Wyzed they will appear here.
                      </p>
                    </div>
                  ) : filteredTraining.length === 0 ? (
                    <div className="px-5 py-12 text-center">
                      <p className="text-sm text-slate-600">
                        No courses match your filters.
                      </p>
                      <button
                        type="button"
                        onClick={clearTrainingFilters}
                        className="mt-2 text-sm font-medium text-indigo-600 hover:text-indigo-800"
                      >
                        Clear filters
                      </button>
                    </div>
                  ) : (
                    <div className="overflow-x-auto max-h-[min(70vh,640px)]">
                      <table className="min-w-full text-sm">
                        <thead className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm">
                          <tr className="border-b border-slate-100 text-left text-xs font-semibold text-slate-500">
                            <SortableHeader
                              label="Course"
                              sortKey="course"
                              activeSort={trainingSort}
                              onSort={handleTrainingSort}
                            />
                            <SortableHeader
                              label="Status"
                              sortKey="status"
                              activeSort={trainingSort}
                              onSort={handleTrainingSort}
                            />
                            <SortableHeader
                              label="Progress"
                              sortKey="progress"
                              activeSort={trainingSort}
                              onSort={handleTrainingSort}
                            />
                            <SortableHeader
                              label="Completed"
                              sortKey="completed"
                              activeSort={trainingSort}
                              onSort={handleTrainingSort}
                            />
                            <th className="px-5 py-3 uppercase tracking-wide">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {displayedTraining.map((row) => (
                            <tr
                              key={row.id}
                              className="hover:bg-slate-50/50 transition-colors"
                            >
                              <td className="px-5 py-4 font-medium text-slate-900">
                                {row.course_name}
                              </td>
                              <td className="px-5 py-4">
                                <span
                                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
                                    STATUS_COLORS[row.status] ?? STATUS_COLORS.assigned
                                  }`}
                                >
                                  {STATUS_LABELS[row.status] ?? row.status}
                                </span>
                              </td>
                              <td className="px-5 py-4">
                                <CourseProgressBar
                                  completed={row.progress_completed}
                                  total={row.progress_total}
                                />
                              </td>
                              <td className="px-5 py-4 text-slate-600 whitespace-nowrap">
                                {row.completed_at
                                  ? formatDateToAustralian(row.completed_at)
                                  : "—"}
                              </td>
                              <td className="px-5 py-4">
                                <div className="flex flex-col items-start gap-2">
                                  {canStart && row.can_start_training && (
                                    <button
                                      type="button"
                                      onClick={() => handleStartTraining(row)}
                                      disabled={
                                        startingId === row.id || isUserGracePeriodOver
                                      }
                                      className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                                    >
                                      <HiOutlineExternalLink className="h-3.5 w-3.5" />
                                      {startingId === row.id
                                        ? "Opening…"
                                        : row.status === "completed"
                                          ? "Open in Wyzed"
                                          : "Start training"}
                                    </button>
                                  )}
                                  {canDownload && row.can_download_certificate && (
                                    <button
                                      type="button"
                                      onClick={() => handleDownloadCertificate(row)}
                                      disabled={
                                        downloadingId === row.id ||
                                        isUserGracePeriodOver
                                      }
                                      className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                                    >
                                      <HiOutlineDocumentDownload className="h-3.5 w-3.5" />
                                      {downloadingId === row.id
                                        ? "Downloading…"
                                        : "Download certificate"}
                                    </button>
                                  )}
                                  {!canStart &&
                                    !(canDownload && row.can_download_certificate) && (
                                      <span className="text-xs text-slate-400">—</span>
                                    )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      <ModalSkeleton
        isOpen={Boolean(startConfirmRow)}
        cssClass="w-[90%] max-w-md"
        title="Open Wyzed"
        closeModal={() => setStartConfirmRow(null)}
        modalFooter={
          <>
            <CustomButton
              label="Cancel"
              variant="secondary"
              clickHandler={() => setStartConfirmRow(null)}
            />
            <CustomButton
              label={
                startingId === startConfirmRow?.id ? "Opening…" : "Continue to Wyzed"
              }
              showLoading={startingId === startConfirmRow?.id}
              isDisabled={
                startingId === startConfirmRow?.id || isUserGracePeriodOver
              }
              clickHandler={() => openWyzedPortal(startConfirmRow)}
            />
          </>
        }
      >
        <div className="space-y-3 text-sm text-slate-600">
          <p>
            Wyzed is a separate website. If you are not already signed in, sign in
            with your Wyzed learner account, then open the course from your list if
            needed.
          </p>
          {wyzedEmail ? (
            <p>
              Use this email to sign in:{" "}
              <span className="font-medium text-slate-900">{wyzedEmail}</span>
            </p>
          ) : (
            <p>Use the email address linked to your Wyzed learner profile.</p>
          )}
          {startConfirmRow?.course_name ? (
            <p>
              You will be taken to{" "}
              <span className="font-medium text-slate-900">
                {startConfirmRow.course_name}
              </span>
              .
            </p>
          ) : null}
        </div>
      </ModalSkeleton>
    </div>
  );
};

export default WorkerWyzedTraining;
